<template>
  <div class="p-0 d-flex justify-content-end">
    <transition name="expand" mode="out-in">
      <div v-if="userModules.comments && visibleTab === 'Comment'" key="comments" class="full-lg">
        <Comments class="side-bar-tab " @showNav="showNav" />
      </div>
      <div v-else-if="userModules.amendments && visibleTab === 'Amendment'" key="amendments"
        class="full-lg d-flex flex-column flex-grow-1">
        <Amendments class="side-bar-tab" @showNav="showNav" />
      </div>
      <div v-else-if="userModules.candidacies && visibleTab === 'Candidacy'" key="candidacies"
        class="full-lg d-flex flex-column flex-grow-1">
        <Candidacies class="side-bar-tab" @showNav="showNav" />
      </div>
      <div v-else-if="visibleTab === 'Locales'" key="locales" class="full-lg d-flex flex-column flex-grow-1">
        <LocaleTab class="side-bar-tab" @showNav="showNav" />
      </div>
    </transition>

    <div class="d-flex flex-column justify-content-between m-0 text-center bg-dark"
      style="position: relative; z-index: 2; min-width: 100px; max-width: 100px;">
      <div>
        <div>
          <SideBarButton v-if="userModules.comments" @click="showTab('Comment')" :isActive="visibleTab === 'Comment'"
            :unread="unreadComments" :text="$t('js.sidebar.headers.comments')" icon="fa-comment" />
          <SideBarButton v-if="userModules.amendments" @click="showTab('Amendment')"
            :isActive="visibleTab === 'Amendment'" :unread="unreadAmendments"
            :text="$t('js.sidebar.headers.amendments')" icon="fa-file-signature" />
          <SideBarButton v-if="userModules.candidacies" @click="showTab('Candidacy')"
            :isActive="visibleTab === 'Candidacy'" :unread="unreadCandidacies"
            :text="$t('js.sidebar.headers.candidacies')" icon="fa-user" />
          <SideBarButton data-bs-toggle="modal" data-bs-target="#highlightModal" :isActive="activeHighlight != null"
            :color="activeHighlight ? 'btn-success' : 'btn-secondary'" @click="setPostToHighlight(null)"
            :text="$t('js.sidebar.headers.highlight')" icon="fa-thumbtack" :pulse="!!activeHighlight" />
        </div>
      </div>
      <div>
        <VoterCounts />
        <a href="." class="w-100 btn rounded-0 py-3 btn-theme-danger" style="line-height: 1em">
          <i class="fas fa-door-open"></i><br>
          <span class="small text-truncate">{{ $t("js.sidebar.general.exit_mode") }}</span>
        </a>
      </div>
    </div>
    <HighlightModal />

    <Modal :visible="!!postToAddToBallot" @close="setPostToAddToBallot(null)" class="w-100 h-100" :maxWidth="'800px'">
      <template v-slot:header>
        <h4 class="mb-0">{{ $t("js.sidebar.candidacies.accept_and_add") }}</h4>
      </template>
      <template v-slot:body>
        <iframe :src="addCandidacyPath" class="w-100 h-100" frameBorder="0" style="min-height: 300px" id="addCandidacy">

        </iframe>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState, mapActions } from "pinia";
import Comments from "./Comments.vue"
import Candidacies from "./Candidacies.vue"
import Amendments from "./Amendments.vue"
import LocaleTab from "@/components/shared/LocaleTab.vue"
import SlideTab from "./SlideTab.vue"
import HighlightModal from "../HighlightModal.vue"
import SideBarButton from "@/components/frontend/voting/sidebar/SideBarButton.vue"
import VoterCounts from "@/components/shared/VoterCounts.vue"
import Modal from "@/components/shared/Modal.vue"
import { SoundEffectPlayer } from "@/entrypoints/shared/soundEffects";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import { useVotingSessionStore } from "@/entrypoints/stores/voting_session";
import { useVotingModulesStore } from "@/entrypoints/stores/voting_modules";
import type { Locale, UserPost, LiveTab } from "@/types";

export default defineComponent({
  name: "SideBar",
  components: {
    Modal,
    VoterCounts,
    SideBarButton,
    HighlightModal,
    SlideTab,
    LocaleTab,
    Amendments,
    Candidacies,
    Comments,
  },
  data() {
    return {
      editPostId: null,
      navVisible: false,
      soundEffectPlayer: new SoundEffectPlayer({ volume: 0.5 }),
    }
  },
  computed: {
    ...mapState(useSharedStore, ["election", "locales"]),
    ...mapState(usePresentationStore, ["activeHighlight", "visibleTab", "contests"]),
    ...mapState(useVotingSessionStore, ["voting"]),
    ...mapState(useVotingModulesStore, ["posts", "activeHighlight", "postToAddToBallot"]),
    availableLocales() {
      return this.$i18n.availableLocales;
    },
    userModules() {
      return this.election.userModules;
    },
    unreadComments() {
      if (this.visibleTab === "Comment") return 0;
      return this.posts.filter((p: UserPost) => p.type === "Comment" && p.unread).length;
    },
    unreadAmendments() {
      return this.posts.filter((p: UserPost) => p.type === "Amendment" && p.unread).length;
    },
    unreadCandidacies() {
      return this.posts.filter((p: UserPost) => p.type === "Candidacy" && p.unread).length;
    },
    addCandidacyPath() {
      return this.postToAddToBallot ? `../contests/${this.postToAddToBallot.target_slide.contestId}/options/simple_new?post_id=${this.postToAddToBallot.id}&${this.locales.map((locale: Locale) => `option[title][${locale}]=${this.postToAddToBallot.owner.name}`).join("&")}` : "";
    },
  },
  methods: {
    ...mapActions(usePresentationStore, ["fetchLatestConfig", "setVisibleTab"]),
    ...mapActions(useVotingModulesStore, ["setPostToHighlight", "setPostToAddToBallot"]),
    showTab(tab: LiveTab) {
      this.navVisible = false;
      if (tab === this.visibleTab) {
        this.setVisibleTab("Presentation");
      } else {
        this.setVisibleTab(tab);
      }
    },
    showNav() {
      this.navVisible = !this.navVisible;
    },
  },
  watch: {
    unreadAmendments: function (newCount, oldCount) {
      if (newCount > oldCount) {
        this.soundEffectPlayer.playAmendmentNotification();
      }
    },
    unreadCandidacies: function (newCount, oldCount) {
      if (newCount > oldCount) {
        this.soundEffectPlayer.playCandidacyNotification();
      }
    },
  },
  created() {
    this.fetchLatestConfig();
  },
});
</script>
